'use client';

import React, { useEffect, useState } from 'react';
import { Building2 } from 'lucide-react';

const SESSION_KEY = 'plenaricat_home_intro_seen';

// Total visible duration: ~2300ms (450ms fade-in, 1300ms hold, 550ms fade-out)
const FADE_IN_MS = 450;
const HOLD_MS = 1300;
const FADE_OUT_MS = 550;

export default function HomeIntroSplash() {
  // null = not yet determined (SSR / first paint)
  // true = show splash
  // false = hidden / done
  const [phase, setPhase] = useState<'hidden' | 'fadein' | 'visible' | 'fadeout' | 'done'>('hidden');
  const [prefersReduced, setPrefersReduced] = useState(false);

  useEffect(() => {
    // Detect reduced motion
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReduced(mq?.matches);

    // Check sessionStorage — if already seen, skip entirely
    try {
      if (sessionStorage.getItem(SESSION_KEY)) {
        return;
      }
    } catch {
      // sessionStorage unavailable (private mode restriction etc.) — skip splash
      return;
    }

    // Mark as seen immediately so reload / back-navigation won't re-show
    try {
      sessionStorage.setItem(SESSION_KEY, '1');
    } catch {
      // ignore
    }

    if (mq?.matches) {
      // Reduced motion: skip animation entirely
      return;
    }

    // Start animation sequence
    setPhase('fadein');

    const holdTimer = setTimeout(() => {
      setPhase('visible');
    }, FADE_IN_MS);

    const fadeOutTimer = setTimeout(() => {
      setPhase('fadeout');
    }, FADE_IN_MS + HOLD_MS);

    const doneTimer = setTimeout(() => {
      setPhase('done');
    }, FADE_IN_MS + HOLD_MS + FADE_OUT_MS);

    return () => {
      clearTimeout(holdTimer);
      clearTimeout(fadeOutTimer);
      clearTimeout(doneTimer);
    };
  }, []);

  // Not showing
  if (phase === 'hidden' || phase === 'done' || prefersReduced) {
    return null;
  }

  const opacity =
    phase === 'fadein' ? 0
    : phase === 'visible' ? 1
    : phase === 'fadeout' ? 0
    : 1;

  const scale =
    phase === 'fadein' ? 0.97
    : 1;

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        backgroundColor: '#0f1f3d', // navy corporatiu
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        opacity,
        transform: `scale(${scale})`,
        transition: `opacity ${phase === 'fadein' ? FADE_IN_MS : FADE_OUT_MS}ms ease, transform ${FADE_IN_MS}ms ease`,
        pointerEvents: 'none', // never captures interaction
      }}
    >
      {/* Logo mark */}
      <div
        style={{
          width: '56px',
          height: '56px',
          borderRadius: '14px',
          backgroundColor: 'rgba(255,255,255,0.10)',
          border: '1px solid rgba(255,255,255,0.15)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '20px',
        }}
      >
        <Building2 size={24} color="rgba(255,255,255,0.85)" />
      </div>

      {/* Wordmark */}
      <p
        style={{
          fontFamily: 'var(--font-inter-tight), system-ui, sans-serif',
          fontSize: '1.625rem',
          fontWeight: 600,
          color: 'rgba(255,255,255,0.92)',
          letterSpacing: '-0.03em',
          lineHeight: 1,
          marginBottom: '8px',
        }}
      >
        PlenariCAT
      </p>

      {/* Subtitle */}
      <p
        style={{
          fontFamily: 'var(--font-dm-sans), system-ui, sans-serif',
          fontSize: '0.8125rem',
          fontWeight: 400,
          color: 'rgba(255,255,255,0.45)',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
        }}
      >
        Treball municipal
      </p>
    </div>
  );
}
